import React, { useEffect, useRef, useState } from 'react';
import { MapContainer, TileLayer, Marker, Popup, ZoomControl, useMap } from 'react-leaflet';
import { fetchDangerZones } from '../../pages/home/_redux/dashboard/Actions';
import { shallowEqual, useSelector, useDispatch } from "react-redux";
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

/**
 * Custom Pulsing Marker Icon
 * Creates animated markers based on severity
 */
const createCustomIcon = (severity, isSelected = false) => {
  const colors = {
    critical: '#F21B3F',
    high: '#ff9f43',
    medium: '#f1c40f',
    low: '#3498db'
  };

  const color = colors[severity] || colors.medium;
  const size = isSelected ? 30 : 20;
  const pulseScale = isSelected ? 4 : 3.5;

  return L.divIcon({
    className: 'custom-marker',
    html: `
      <div class="marker-container ${isSelected ? 'selected' : ''}">
        <div class="marker-pulse" style="background-color: ${color}; animation: markerPulseAnimate ${isSelected ? '1.5s' : '2s'} infinite ease-out;"></div>
        <div class="marker-core" style="background-color: ${color}; width: ${size/2}px; height: ${size/2}px;"></div>
      </div>
      <style>
        @keyframes markerPulseAnimate {
          0% { transform: scale(1); opacity: 0.6; }
          100% { transform: scale(${pulseScale}); opacity: 0; }
        }
      </style>
    `,
    iconSize: [size, size],
    iconAnchor: [size/2, size/2],
  });
};

/**
 * Component to handle map updates when selected incident changes
 */
function MapController({ selectedIncident }) {
  const map = useMap();
  const initialView = { center: [9.0820, 8.6753], zoom: 6 };
  
  useEffect(() => {
    if (selectedIncident && selectedIncident.coordinates) {
      // FIX: Swap coordinates here too - API gives [lng, lat], Leaflet needs [lat, lng]
      const leafletCoords = [selectedIncident.coordinates[1], selectedIncident.coordinates[0]];
      map.flyTo(leafletCoords, 12, {
        duration: 1.5,
        easeLinearity: 0.25
      });
    } else {
      map.flyTo(initialView.center, initialView.zoom, {
        duration: 2,
        easeLinearity: 0.20
      });
    }
  }, [selectedIncident, map, initialView.center, initialView.zoom]);

  return null;
}

/**
 * Main Danger Zone Map Component
 */
function DangerZoneMap({ onIncidentSelect, selectedIncident, theme }) {
  const dispatch = useDispatch();
  const mapRef = useRef(null);
  const [mapStyle, setMapStyle] = useState('dark');

  const { incidents,
    //  error, loading 
    } = useSelector(
    (state) => ({
      incidents: state.dashboard.danger_zones || [],
      // loading: state.dashboard.listLoading,
      // error: state.dashboard.error
    }),
    shallowEqual
  );

  useEffect(() => {
    // Fetch recent incidents (last 24 hours, limit 50)
    dispatch(fetchDangerZones({
      hours: 24,
      limit: 50,
      severity_min: 1
    }));

    // Optional: Set up auto-refresh every 5 minutes
    const refreshInterval = setInterval(() => {
      dispatch(fetchDangerZones({
        hours: 24,
        limit: 50,
        severity_min: 1
      }));
    }, 5 * 60 * 1000); // 5 minutes

    return () => clearInterval(refreshInterval);
  }, [dispatch]);

  // Update map style based on theme
  useEffect(() => {
    setMapStyle(theme === 'dark' ? 'dark' : 'light');
  }, [theme]);

  // Get appropriate tile layer based on theme
  const getTileLayer = () => {
    if (mapStyle === 'dark') {
      return {
        url: "https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png",
        attribution: '&copy; <a href="https://carto.com/">CARTO</a>'
      };
    } else {
      return {
        url: "https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png",
        attribution: '&copy; <a href="https://carto.com/">CARTO</a>'
      };
    }
  };

  const tileLayer = getTileLayer();


  const handleShareIncident = async (incident) => {
    const shareData = {
      title: `Sety Security Alert: ${incident.type}`,
      text: `⚠️ SECURITY ALERT: ${incident.type.toUpperCase()}\n📍 Location: ${incident.location}\n🕒 Time: ${incident.time}\n\nStay safe and get real-time intelligence via Sety.`,
      // url: `https://dasety.io/intel/${incident.id}` // Your dynamic link
      url: `https://danger.sety.io/` // Your dynamic link
    };

    try {
      if (navigator.share) {
        // Use Native Device Share
        await navigator.share(shareData);
      } else {
        // Fallback: Copy to clipboard if Web Share is not supported (e.g., some desktop browsers)
        const shareText = `${shareData.text}\nView live map: ${shareData.url}`;
        await navigator.clipboard.writeText(shareText);
        
        // Use that clean toast we created earlier!
        // setStatus({ type: "success", message: "Alert copied to clipboard!" });
        alert("Alert link copied to clipboard!"); 
      }
    } catch (err) {
      console.log("Error sharing:", err);
    }
  };

  return (
    <div className="map-engine-wrapper">
      <MapContainer 
        ref={mapRef}
        center={[9.0820, 8.6753]} 
        zoom={6} 
        zoomControl={false}
        style={{ height: '100%', width: '100%' }}
        minZoom={5}
        maxZoom={18}
      >
        {/* Base Map Layer */}
        <TileLayer
          url={tileLayer.url}
          attribution={tileLayer.attribution}
        />

        {/* Map Controller for Selected Incident */}
        <MapController selectedIncident={selectedIncident} />

        {/* Incident Markers */}
        {incidents.map((incident) => {
          // CRITICAL FIX: Swap coordinates
          // API returns: [longitude, latitude] = [3.5642897, 8.2151249]
          // Leaflet needs: [latitude, longitude] = [8.2151249, 3.5642897]
          const leafletCoordinates = [incident.coordinates[1], incident.coordinates[0]];
          
          return (
            <Marker 
              key={incident.id} 
              position={leafletCoordinates}
              icon={createCustomIcon(
                incident.severity, 
                selectedIncident?.id === incident.id
              )}
              // eventHandlers={{
              //   click: () => onIncidentSelect(incident),
              // }}
              eventHandlers={{
                click: () => onIncidentSelect(incident),
                // THIS RESET LOGIC:
                popupclose: () => onIncidentSelect(null), 
              }}
            >
              <Popup className="pro-popup" maxWidth={300}>
                <div className="popup-content">
                  <div className="popup-header">
                    <span className={`status-pill ${incident.severity}`}>
                      {incident.type}
                    </span>
                    {incident.verified && (
                      <span className="status-pill verified">
                        <i className="fa-solid fa-shield-check"></i> Verified
                      </span>
                    )}
                  </div>
                  
                  <h4>
                    <i className="fa-solid fa-location-dot"></i>
                    {incident.location}
                  </h4>
                  
                  <p className="popup-time">
                    <i className="fa-solid fa-clock"></i>
                    {incident.time}
                  </p>
                  
                  <p className="popup-details">{incident.details}</p>
                  
                  <div className="popup-actions">
                    <button 
                      className="popup-btn primary"
                      onClick={() => onIncidentSelect(incident)}
                    >
                      <i className="fa-solid fa-eye"></i>
                      View Details
                    </button>
                    <button 
                      onClick={(e) => {
                        e.stopPropagation(); // Prevent map click events
                        handleShareIncident(incident);
                      }}
                    className="popup-btn secondary">
                      <i className="fa-solid fa-share-nodes"></i>
                      Share
                    </button>
                  </div>
                </div>
              </Popup>
            </Marker>
          );
        })}

        {/* Zoom Control (Bottom Left) */}
        <ZoomControl position="bottomleft" />
      </MapContainer>

      {/* Map Legend */}
      <div className="map-legend">
        <h5>Threat Levels</h5>
        <div className="legend-items">
          <div className="legend-item">
            <span className="legend-dot critical"></span>
            <span>Critical</span>
          </div>
          <div className="legend-item">
            <span className="legend-dot high"></span>
            <span>High</span>
          </div>
          <div className="legend-item">
            <span className="legend-dot medium"></span>
            <span>Medium</span>
          </div>
          <div className="legend-item">
            <span className="legend-dot low"></span>
            <span>Low</span>
          </div>
        </div>
      </div>

      {/* Live Incident Counter */}
      <div className="map-stats">
        <div className="stat-item">
          <div className="stat-icon critical">
            <i className="fa-solid fa-triangle-exclamation"></i>
          </div>
          <div className="stat-content">
            <span className="stat-value">{incidents.filter(i => i.severity === 'critical').length}</span>
            <span className="stat-label">Critical Alerts</span>
          </div>
        </div>
        <div className="stat-divider"></div>
        <div className="stat-item">
          <div className="stat-icon total">
            <i className="fa-solid fa-tower-broadcast"></i>
          </div>
          <div className="stat-content">
            <span className="stat-value">{incidents.length}</span>
            <span className="stat-label">Total Active</span>
          </div>
        </div>
      </div>
    </div>
  );
}

export default DangerZoneMap;